/**
 * Utility functions for determining status labels for PTO suggestions
 */

import { PTOSuggestion } from '../../../types'

/**
 * Status label types
 */
export enum StatusLabelType {
  BEST_OPTION = 'bestOption',
  QUICK_WIN = 'quickWin',
  GOOD_OPTION = 'goodOption',
  DEEP_RECOVERY = 'deepRecovery',
}

/**
 * Configuration for status label thresholds
 */
export interface StatusLabelConfig {
  quickWinPtoDays: number // PTO days for quick win (default: 1)
  goodOptionMinPtoDays: number // Minimum PTO days for good option (default: >1)
  goodOptionMaxPtoDays: number // Maximum PTO days for good option (default: <3)
  deepRecoveryMinPtoDays: number // Minimum PTO days for deep recovery (default: >5)
}

/**
 * Default configuration for status label thresholds
 */
export const DEFAULT_STATUS_LABEL_CONFIG: StatusLabelConfig = {
  quickWinPtoDays: 1,
  goodOptionMinPtoDays: 1,
  goodOptionMaxPtoDays: 3,
  deepRecoveryMinPtoDays: 5,
}

/**
 * Determines the status label type for a PTO suggestion based on configuration
 *
 * Priority order:
 * 1. If isBestOption is true, return "bestOption"
 * 2. If there is 1 PTO day with weekend, return "quickWin"
 * 3. If there are >1 and <3 PTO days used + weekend, return "goodOption"
 * 4. If more than configured PTO days used, return "deepRecovery"
 * 5. Default to "goodOption"
 *
 * @param suggestion - The PTO suggestion
 * @param config - Optional configuration for thresholds (defaults to DEFAULT_STATUS_LABEL_CONFIG)
 * @returns The status label type
 */
export function getStatusLabelType(suggestion: PTOSuggestion, config: StatusLabelConfig = DEFAULT_STATUS_LABEL_CONFIG): StatusLabelType {
  const { isBestSuggestion, ptoDaysUsed, weekendsIncluded } = suggestion

  // Priority 1: Best option
  if (isBestSuggestion) {
    return StatusLabelType.BEST_OPTION
  }

  const hasWeekends = weekendsIncluded && weekendsIncluded.length > 0

  // Priority 2: Quick win - 1 PTO day with weekend
  if (hasWeekends && ptoDaysUsed === config.quickWinPtoDays) {
    return StatusLabelType.QUICK_WIN
  }

  // Priority 3: Good option - >1 and <3 PTO days with weekend
  if (hasWeekends && ptoDaysUsed > config.goodOptionMinPtoDays && ptoDaysUsed < config.goodOptionMaxPtoDays) {
    return StatusLabelType.GOOD_OPTION
  }

  // Priority 4: Deep recovery - more than configured PTO days
  if (ptoDaysUsed > config.deepRecoveryMinPtoDays) {
    return StatusLabelType.DEEP_RECOVERY
  }

  // Default: Good option
  return StatusLabelType.GOOD_OPTION
}
